import { runInAction, set } from 'mobx';
import { observer } from 'mobx-react-lite';
import type { JSX } from 'react';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { Select } from '@feathr/components';

import { getUnusedColumns, matchBuiltInField } from './DataImportStepTwo.utils';
import type { IImportMappingCellProps } from './ImportMappingTable.utils';

/** The mapping row's file column, picked from its own column and the ones no other row uses. */
function ImportMappingColumnCell({
  column,
  importer,
  disabled,
}: Readonly<IImportMappingCellProps>): JSX.Element {
  const { t } = useTranslation();
  const columnNames = importer.get('column_names') ?? [];
  const otherRows = importer.get('column_mappers').filter((mapper) => mapper !== column);
  const unused = new Set(getUnusedColumns(columnNames, otherRows));
  const options = columnNames
    .filter((name) => name === column.column_name || unused.has(name))
    .map((name) => ({ id: name, name }));

  function handleSelectColumn(option: { id: string; name: string }): void {
    runInAction(() => {
      set(column, 'column_name', option.id);
    });
    // The field cell auto-maps only when its row mounts, before a new row has a column.
    matchBuiltInField(column);
    importer.setAttributeDirty('column_mappers');
  }

  return (
    <Select
      aria-label={t('Column in your file')}
      disabled={disabled}
      // Table cells clip overflow, so the menu renders on the body.
      menuPortalTarget={document.body}
      name={'column-name'}
      onSelectSingle={handleSelectColumn}
      options={options}
      placeholder={t('Select a column...')}
      value={options.find((option) => option.id === column.column_name)}
    />
  );
}

export default observer(ImportMappingColumnCell);
